import {
	AppFooter,
	AppHeader,
	AppToastProvider,
	NotFoundPage,
	PageLayout,
	SearchField,
} from "@homesite/ui";
import {
	Link,
	Outlet,
	createRootRoute,
	useNavigate,
	useRouterState,
} from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useState } from "react";

const NAV_ITEMS = [
	{ label: "Verify", href: "/" },
	{ label: "Loans", href: "/loans" },
	{ label: "Reports & certificates", href: "/reports" },
	{ label: "How it works", href: "/help" },
];

const HELP_LINKS = [{ label: "Help", href: "/help" }];

const FOOTER_DESCRIPTION =
	"This public portal lets you independently confirm that a loan's records are authentic and have not been altered.";

const EVENT_HASH_RE = /^0x[a-fA-F0-9]{64}$/;

/** App-level search wiring: routes hashes to the event view, anything else to a loan. */
function HeaderSearch({
	onNavigate,
}: {
	onNavigate?: () => void;
}): ReactNode {
	const navigate = useNavigate();
	const [value, setValue] = useState("");

	function handleSubmit(event: FormEvent<HTMLFormElement>): void {
		event.preventDefault();
		const trimmed = value.trim();
		if (!trimmed) return;
		if (EVENT_HASH_RE.test(trimmed)) {
			navigate({ to: "/events/$hash", params: { hash: trimmed } });
		} else {
			navigate({ to: "/loans/$loanId", params: { loanId: trimmed } });
		}
		onNavigate?.();
	}

	return (
		// biome-ignore lint/a11y/useSemanticElements: role="search" is the canonical WAI-ARIA search landmark; the suggested <input type="search"> is not landmark-equivalent.
		<form role="search" onSubmit={handleSubmit} className="w-full">
			<SearchField
				value={value}
				onChange={setValue}
				placeholder="Find a loan or event"
				aria-label="Find a loan or event"
				onClear={value ? () => setValue("") : undefined}
			/>
			<button type="submit" className="sr-only">
				Search
			</button>
		</form>
	);
}

export const rootRoute = createRootRoute({
	component: function Root() {
		const pathname = useRouterState({
			select: (s) => s.location.pathname,
		});

		const navItems = NAV_ITEMS.map((item) => ({
			...item,
			isActive:
				item.href === "/" ? pathname === "/" : pathname.startsWith(item.href),
		}));

		return (
			<AppToastProvider>
				<PageLayout
					header={
						<AppHeader
							logoSlot={
								<Link
									to="/"
									aria-label="RevUp home"
									className="flex items-center gap-3"
								>
									<img
										src="https://revupportal.com/assets/images/revup-logo.jpg"
										alt="RevUp"
										className="h-8 w-auto"
									/>
								</Link>
							}
							navItems={navItems}
							search={({ closeMobileMenu }) => (
								<HeaderSearch onNavigate={closeMobileMenu} />
							)}
						/>
					}
					footer={
						<AppFooter links={HELP_LINKS} description={FOOTER_DESCRIPTION} />
					}
				>
					<Outlet />
				</PageLayout>
			</AppToastProvider>
		);
	},
	notFoundComponent: function NotFound() {
		return <NotFoundPage homeHref="/" />;
	},
});
